import { motion } from "framer-motion";
import {
    Brain,
    Dumbbell,
    TrendingUp,
    Apple as AppleIcon,
    PlayCircle,
} from "lucide-react";
import { FEATURES } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const iconMap = {
    brain: Brain,
    dumbbell: Dumbbell,
    trending: TrendingUp,
    apple: AppleIcon,
    play: PlayCircle,
};

export const Features = () => {
    const { t } = useLang();

    return (
        <section
            id="features"
            data-testid="features-section"
            className="relative py-14 sm:py-20 bg-[var(--bg)]"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="max-w-2xl mb-12 sm:mb-16"
                >
                    <span className="font-display text-[var(--primary)] text-sm tracking-[0.3em]">
                        {t.features.eyebrow}
                    </span>
                    <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                        {t.features.h2Top}
                        <br />
                        <span className="text-gradient-orange">{t.features.h2Bottom}</span>
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-neutral-400 leading-relaxed">
                        {t.features.desc}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-2 gap-5 sm:gap-6 auto-rows-[280px]">
                    {FEATURES.map((f, i) => {
                        const Icon = iconMap[f.icon] || Brain;
                        const text = t.features.items[f.id];
                        return (
                            <motion.div
                                key={f.id}
                                data-testid={`feature-${f.id}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className={`card-dark relative rounded-2xl overflow-hidden group min-h-[260px] ${f.span}`}
                            >
                                <div className="absolute inset-0">
                                    <img
                                        src={f.image}
                                        alt={text.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                                </div>

                                <div className="relative h-full p-6 sm:p-8 flex flex-col justify-end">
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-[var(--primary)]/15 border border-[var(--primary)]/30 mb-4 group-hover:bg-[var(--primary)] transition-colors">
                                        <Icon className="w-6 h-6 text-[var(--primary)] group-hover:text-black transition-colors" />
                                    </div>
                                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight">
                                        {text.title}
                                    </h3>
                                    <p className="text-sm sm:text-base text-neutral-300 leading-relaxed max-w-md">
                                        {text.desc}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
